import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import wsClient from '../utils/websocket';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reservations = () => {
  const { selectedProperty } = useOutletContext();
  const { t } = useTranslation();
  const [reservations, setReservations] = useState([]);
  const [guests, setGuests] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [wsConnected, setWsConnected] = useState(false);
  
  const [formData, setFormData] = useState({
    guest_id: '',
    unit_id: '',
    check_in: '',
    check_out: '',
    adults: 1,
    children: 0,
    base_amount: 0,
    tax_amount: 0,
    source: 'Direct',
    status: 'CONFIRMED',
    notes: ''
  });

  useEffect(() => {
    if (selectedProperty) {
      fetchData();
    }
  }, [selectedProperty]);


  // WebSocket integration for real-time updates
  useEffect(() => {
    if (!selectedProperty) return;

    const token = localStorage.getItem('access_token');
    if (!token) return;

    // Connect WebSocket (reuse Calendar's connection)
    wsClient.connect(selectedProperty.id, token);

    // Handle connection status
    const handleConnection = (data) => {
      setWsConnected(data.connected);
    };

    // Handle reservation events
    const handleReservationEvent = (event) => {
      console.log('[Reservations] Event received:', event.type);
      fetchData(); // Refetch reservations list
    };

    // Register handlers
    wsClient.on('connection', handleConnection);
    wsClient.on('reservation.created', handleReservationEvent);
    wsClient.on('reservation.updated', handleReservationEvent);
    wsClient.on('reservation.deleted', handleReservationEvent);
    wsClient.on('guest.checked_in', handleReservationEvent);
    wsClient.on('guest.checked_out', handleReservationEvent);

    // Polling fallback - 90 seconds
    const pollingInterval = setInterval(() => {
      if (!wsConnected) {
        console.log('[Reservations] Polling fallback');
        fetchData();
      }
    }, 90000);

    return () => {
      wsClient.off('connection', handleConnection);
      wsClient.off('reservation.created', handleReservationEvent);
      wsClient.off('reservation.updated', handleReservationEvent);
      wsClient.off('reservation.deleted', handleReservationEvent);
      wsClient.off('guest.checked_in', handleReservationEvent);
      wsClient.off('guest.checked_out', handleReservationEvent);
      clearInterval(pollingInterval);
    };
  }, [selectedProperty]);


  const fetchData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const [resRes, guestsRes, unitsRes] = await Promise.all([
        axios.get(`${API}/reservations?property_id=${selectedProperty.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/guests`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/properties/${selectedProperty.id}/units`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setReservations(resRes.data);
      setGuests(guestsRes.data);
      setUnits(unitsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReservation = async (e) => {
    e.preventDefault();
    
    if (!formData.guest_id) {
      toast.error('Please select a guest');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API}/reservations`,
        { ...formData, property_id: selectedProperty.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reservation created successfully');
      setDialogOpen(false);
      setFormData({
        guest_id: '',
        unit_id: '',
        check_in: '',
        check_out: '',
        adults: 1,
        children: 0,
        base_amount: 0,
        tax_amount: 0,
        source: 'Direct',
        status: 'CONFIRMED',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create reservation');
    }
  };

  const handleStatusChange = async (reservationId, newStatus) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(
        `${API}/reservations/${reservationId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      TENTATIVE: 'bg-amber-100 text-amber-700 border-amber-300',
      CONFIRMED: 'bg-cyan-100 text-cyan-700 border-cyan-300',
      IN_HOUSE: 'bg-emerald-100 text-emerald-700 border-emerald-300',
      CHECKED_OUT: 'bg-slate-100 text-slate-700 border-slate-300',
      CANCELLED: 'bg-red-100 text-red-700 border-red-300'
    };
    return styles[status] || styles.CONFIRMED;
  };

  const getGuestName = (guestId) => {
    const guest = guests.find(g => g.id === guestId);
    return guest?.name || 'Unknown Guest';
  };

  const getUnitName = (unitId) => {
    if (!unitId) return 'TBA';
    const unit = units.find(u => u.id === unitId);
    return unit?.name || 'Unknown';
  };

  const filteredReservations = reservations.filter(res => {
    const guestName = getGuestName(res.guest_id).toLowerCase();
    const unitName = getUnitName(res.unit_id).toLowerCase();
    const search = searchTerm.toLowerCase();
    return guestName.includes(search) || unitName.includes(search) || res.status.toLowerCase().includes(search);
  });

  if (!selectedProperty) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-slate-600">Please select a property</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-full">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="reservations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Reservations</h1>
          <p className="text-slate-600 mt-1">{filteredReservations.length} reservation(s)</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="create-reservation-button">
              <Plus size={18} />
              New Reservation
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Reservation</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateReservation} className="space-y-4" data-testid="reservation-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Guest *</Label>
                  <Select value={formData.guest_id} onValueChange={(val) => setFormData({...formData, guest_id: val})}>
                    <SelectTrigger data-testid="guest-select">
                      <SelectValue placeholder="Select guest" />
                    </SelectTrigger>
                    <SelectContent>
                      {guests.map(guest => (
                        <SelectItem key={guest.id} value={guest.id} data-testid={`guest-option-${guest.id}`}>
                          {guest.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Room</Label>
                  <Select value={formData.unit_id || 'unassigned'} onValueChange={(val) => setFormData({...formData, unit_id: val === 'unassigned' ? '' : val})}>
                    <SelectTrigger data-testid="unit-select">
                      <SelectValue placeholder="Assign later" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {units.map(unit => (
                        <SelectItem key={unit.id} value={unit.id}>
                          {unit.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Check-in *</Label>
                  <Input
                    type="date"
                    value={formData.check_in}
                    onChange={(e) => setFormData({...formData, check_in: e.target.value})}
                    required
                    data-testid="checkin-input"
                  />
                </div>
                <div>
                  <Label>Check-out *</Label>
                  <Input
                    type="date"
                    value={formData.check_out}
                    onChange={(e) => setFormData({...formData, check_out: e.target.value})}
                    required
                    data-testid="checkout-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Adults</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.adults}
                    onChange={(e) => setFormData({...formData, adults: parseInt(e.target.value)})}
                    data-testid="adults-input"
                  />
                </div>
                <div>
                  <Label>Children</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.children}
                    onChange={(e) => setFormData({...formData, children: parseInt(e.target.value)})}
                    data-testid="children-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Base Amount</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.base_amount}
                    onChange={(e) => setFormData({...formData, base_amount: parseFloat(e.target.value)})}
                    data-testid="amount-input"
                  />
                </div>
                <div>
                  <Label>Tax Amount</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.tax_amount}
                    onChange={(e) => setFormData({...formData, tax_amount: parseFloat(e.target.value)})}
                    data-testid="tax-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Source</Label>
                  <Select value={formData.source} onValueChange={(val) => setFormData({...formData, source: val})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Direct">Direct</SelectItem>
                      <SelectItem value="Phone">Phone</SelectItem>
                      <SelectItem value="Email">Email</SelectItem>
                      <SelectItem value="OTA">OTA</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(val) => setFormData({...formData, status: val})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TENTATIVE">Tentative</SelectItem>
                      <SelectItem value="CONFIRMED">Confirmed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Optional notes"
                />
              </div>

              <Button type="submit" className="w-full" data-testid="submit-reservation-button">
                Create Reservation
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <Input
            placeholder="Search by guest, room, or status..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
      </div>

      <Card data-testid="reservations-list">
        <CardContent className="p-0">
          {filteredReservations.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No reservations found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Guest</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Room</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Check-in</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Check-out</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Guests</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Amount</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Status</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReservations.map((res) => (
                    <tr key={res.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`reservation-row-${res.id}`}>
                      <td className="p-4">
                        <div className="font-medium text-slate-900">{getGuestName(res.guest_id)}</div>
                        <div className="text-xs text-slate-500">{res.source}</div>
                      </td>
                      <td className="p-4 text-slate-700">{getUnitName(res.unit_id)}</td>
                      <td className="p-4 text-slate-700">{format(new Date(res.check_in), 'MMM dd, yyyy')}</td>
                      <td className="p-4 text-slate-700">{format(new Date(res.check_out), 'MMM dd, yyyy')}</td>
                      <td className="p-4 text-slate-700">{res.adults}A / {res.children}C</td>
                      <td className="p-4 font-medium text-slate-900">${res.base_amount + res.tax_amount}</td>
                      <td className="p-4">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(res.status)}`}>
                          {res.status}
                        </span>
                      </td>
                      <td className="p-4">
                        <Select value={res.status} onValueChange={(val) => handleStatusChange(res.id, val)}>
                          <SelectTrigger className="w-32" data-testid={`status-select-${res.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="TENTATIVE">Tentative</SelectItem>
                            <SelectItem value="CONFIRMED">Confirmed</SelectItem>
                            <SelectItem value="IN_HOUSE">In-House</SelectItem>
                            <SelectItem value="CHECKED_OUT">Checked Out</SelectItem>
                            <SelectItem value="CANCELLED">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Reservations;
